import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Button from "../../Button";
import { useCountdown } from "../../../utils/hooks/useCountdown";
import useLogout from "../useLogout";
import { getStorageAuth, saveStorageAuth } from "../methods";
import axios from "../../../axios";
import { closeModalAction } from "../../Modal/reducer";
import PageMetaTagTitle from "../../PageMetaTagTitle";
import { userAuthenticatedAction } from "../reducer";
import AuthService from "../AuthService";
import { MODAL_SESSION_NAME, SESSION_EVENT_KEY, SESSION_EVENTS } from "./index";

const TIME_TO_CONTINUE_YOUR_SESSION = 120;

const ModalSessionContent = ({ onModalClose }) => {
  const { t } = useTranslation();
  const countdownValue = useCountdown(TIME_TO_CONTINUE_YOUR_SESSION);
  const { submit, loading } = useLogout(() => {
    setLoggedOut(true);
  });
  const [loadingContinueSession, setLoadingContinueSession] = useState(false);
  const [loggedOut, setLoggedOut] = useState(false);
  const [sessionExtended, setSessionExtended] = useState(false);
  const [isSelf, setIsSelf] = useState(false);
  const dispatch = useDispatch();

  const continueSession = () => {
    if (loadingContinueSession) {
      return;
    }

    setIsSelf(true);
    setLoadingContinueSession(true);
    AuthService.setManualRefresh(true);

    const authStorage = getStorageAuth();
    axios
      .post("auth/refresh", { token: authStorage.refresh_token }, { headers: { notTracking: true } })
      .then(
        (res) => {
          saveStorageAuth({ ...res.data, google2fa: authStorage.google2fa, token_timestamp: new Date().toString() });
          AuthService.trackTokenValidityTime();
          localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify({ type: SESSION_EVENTS.CONTINUE, ts: Date.now() }));

          setSessionExtended(true);
          setLoggedOut(false);
          onModalClose?.("continue");
          dispatch(closeModalAction({ name: MODAL_SESSION_NAME }));
        },
        () => {
          setLoadingContinueSession(false);
        }
      )
      .finally(() => {
        AuthService.setManualRefresh(false);
        setLoadingContinueSession(false);
      });
  };

  const onLogoutClick = () => {
    localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify({ type: SESSION_EVENTS.LOGOUT, ts: Date.now() }));

    onModalClose?.("logout");
    dispatch(closeModalAction({ name: MODAL_SESSION_NAME }));
    submit();
  };

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== SESSION_EVENT_KEY || !event.newValue) return;
      if (isSelf) return;

      const { type } = JSON.parse(event.newValue);

      if (type === SESSION_EVENTS.CONTINUE) {
        setSessionExtended(true);
        setLoggedOut(false);

        const authStorage = getStorageAuth(); // get token from localStorage
        if (authStorage.user) dispatch(userAuthenticatedAction(authStorage.user));

        axios.defaults.headers.Authorization = `Bearer ${authStorage.access_token}`;
        globalThis.dispatchEvent(new Event("reset-idle-timer")); // custom event pentru CheckToken
        dispatch(closeModalAction({ name: MODAL_SESSION_NAME }));
      }

      if (type === SESSION_EVENTS.LOGOUT && !loggedOut) {
        setLoggedOut(true);
        dispatch(closeModalAction({ name: MODAL_SESSION_NAME }));
        submit();
      }
    };
    globalThis.addEventListener("storage", onStorage);

    return () => globalThis.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedOut, isSelf]);

  useEffect(() => {
    if (loggedOut || sessionExtended) return;

    if (countdownValue === 0) {
      localStorage.setItem(SESSION_EVENT_KEY, JSON.stringify({ type: SESSION_EVENTS.LOGOUT, ts: Date.now() }));

      setLoggedOut(true);
      onModalClose?.("logout");
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdownValue, loggedOut, sessionExtended]);

  return loggedOut ? (
    <>
      <PageMetaTagTitle defer={false} appName="" title={t("container.auth_modal.expired_session")} />
      <div className="h3">{t("container.auth_modal.expired_session")}</div>
      <div className="mt-30 flex">
        <Button onClick={() => dispatch(closeModalAction({ name: MODAL_SESSION_NAME }))}>
          {t("container.general_words.close")}
        </Button>
      </div>
    </>
  ) : (
    <>
      <PageMetaTagTitle defer={false} appName="" title={`${countdownValue} seconds until session expiry`} />
      <div className="h3">
        {t("container.auth_modal.form_title_part1")} {countdownValue} {t("container.auth_modal.form_title_part2")}
      </div>
      <div className="text-inline-small fs-14">{t("container.auth_modal.form_subtitle")}</div>
      <div className="mt-30 flex flex-justify-between">
        <Button loading={loading} disabled={loading} onClick={onLogoutClick} className="close-session-modal">
          {t("container.sidebar.logout")}
        </Button>
        <Button
          className="close-session-modal"
          type="primary"
          disabled={loading || loadingContinueSession}
          loading={loadingContinueSession}
          onClick={continueSession}
        >
          {t("container.auth_modal.continue_session")}
        </Button>
      </div>
    </>
  );
};

export default ModalSessionContent;
